import React, { useState } from "react";
import { NavLink, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Store, Users, MessageSquare, LifeBuoy, ShieldAlert, GraduationCap,
  ShoppingBag, Settings, Menu, X, IdCard, Bell, ScrollText, UserCircle,
  Trophy, Gift, Ticket, BarChart3, Activity, ShieldCheck, Pin, PinOff, ChevronDown,
  Globe, Building2, CreditCard, Wrench, Wallet, Package, MapPin, FileText, UserCog, Sparkles,
} from "lucide-react";
import { ListChecks, Mail, QrCode } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./common";
import { OnboardingWizard } from "./Onboarding";
import InstallPrompt from "./InstallPrompt";
import { usePageMeta } from "../lib/seo";
import {
  useTimeZonesReady, useViewportMode, usePinnedSidebar, useLayoutData, useLiveNotes, ImpersonationBanner, PlanBadge,
  SidebarAccount, ViewToggle, SoundToggle, NotificationsMenu, UserChip, HeaderOrg,
} from "./AppLayoutParts";

function NavItem({ it, onNavigate }) {
  const slug = it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <NavLink
      to={it.to}
      onClick={onNavigate}
      data-testid={`nav-${slug}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
          isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
    >
      <it.icon className="h-[18px] w-[18px]" />
      {it.label}
    </NavLink>
  );
}

function NavGroup({ section, items, onNavigate }) {
  const key = `sv_nav_${section}`;
  const [open, setOpen] = useState(() => localStorage.getItem(key) !== "0");
  const toggle = () => setOpen((o) => { localStorage.setItem(key, o ? "0" : "1"); return !o; });
  return (
    <div className="mb-1">
      <button onClick={toggle} data-testid={`nav-group-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
        className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 transition-colors hover:text-slate-600">
        {section}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="mt-0.5 flex flex-col gap-0.5">{items.map((it) => <NavItem key={it.to} it={it} onNavigate={onNavigate} />)}</div>}
    </div>
  );
}

const NAV = {
  corporate_admin: [
    { section: "Overview", items: [
      { to: "/app/corporate", label: "Dashboard", icon: LayoutDashboard },
      { to: "/app/ai", label: "Servantage AI", icon: Sparkles },
    ]},
    { section: "People", items: [
      { to: "/app/employees", label: "Employees", icon: Users },
      { to: "/app/counters-tables", label: "Counters & Tables", icon: QrCode },
      { to: "/app/team", label: "Team & Access", icon: UserCog },
      { to: "/app/training", label: "Training", icon: GraduationCap },
    ]},
    { section: "Feedback & Recovery", items: [
      { to: "/app/feedback", label: "Feedback", icon: MessageSquare },
      { to: "/app/reviews", label: "Reviews Feed", icon: Globe },
      { to: "/app/questions", label: "Rating Form & Recovery", icon: ListChecks },
      { to: "/app/recovery", label: "Service Recovery", icon: LifeBuoy },
      { to: "/app/disputes", label: "Disputes", icon: ShieldAlert },
      { to: "/app/trust", label: "Trust Center", icon: ShieldCheck },
    ]},
    { section: "Insights", items: [
      { to: "/app/scorecards/executive", label: "Executive Scorecard", icon: Building2 },
      { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/app/leaderboards", label: "Leaderboards", icon: Trophy },
      { to: "/app/store-kpi", label: "Store KPI Ranking", icon: Store },
      { to: "/app/rewards", label: "Team Rewards", icon: Gift },
      { to: "/app/vouchers", label: "Customer Vouchers", icon: Ticket },
      { to: "/app/benchmarking", label: "Benchmarking", icon: Activity },
      { to: "/app/reports", label: "Reports", icon: FileText },
    ]},
    { section: "Setup", items: [
      { to: "/app/locations", label: "Locations", icon: MapPin },
      { to: "/app/merchandise", label: "Merchandise", icon: ShoppingBag },
      { to: "/app/billing", label: "Plan & Billing", icon: CreditCard },
      { to: "/app/settings", label: "Settings", icon: Settings },
      { to: "/app/my-notifications", label: "My Notifications", icon: Bell },
      { to: "/app/audit", label: "Audit Log", icon: ScrollText },
    ]},
  ],
  store_admin: [
    { section: "Overview", items: [
      { to: "/app/store", label: "Dashboard", icon: LayoutDashboard },
      { to: "/app/ai", label: "Servantage AI", icon: Sparkles },
    ]},
    { section: "People", items: [
      { to: "/app/employees", label: "Employees", icon: Users },
      { to: "/app/counters-tables", label: "Counters & Tables", icon: QrCode },
      { to: "/app/team", label: "Team & Access", icon: UserCog },
      { to: "/app/training", label: "Training", icon: GraduationCap },
    ]},
    { section: "Feedback & Recovery", items: [
      { to: "/app/feedback", label: "Feedback", icon: MessageSquare },
      { to: "/app/reviews", label: "Reviews Feed", icon: Globe },
      { to: "/app/questions", label: "Rating Form & Recovery", icon: ListChecks },
      { to: "/app/recovery", label: "Service Recovery", icon: LifeBuoy },
      { to: "/app/disputes", label: "Disputes", icon: ShieldAlert },
      { to: "/app/trust", label: "Trust Center", icon: ShieldCheck },
    ]},
    { section: "Insights", items: [
      { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/app/leaderboards", label: "Leaderboards", icon: Trophy },
      { to: "/app/store-kpi", label: "Store KPI Ranking", icon: Store },
      { to: "/app/rewards", label: "Team Rewards", icon: Gift },
      { to: "/app/vouchers", label: "Customer Vouchers", icon: Ticket },
      { to: "/app/reports", label: "Reports", icon: FileText },
    ]},
    { section: "Setup", items: [
      { to: "/app/merchandise", label: "Merchandise", icon: ShoppingBag },
      { to: "/app/billing", label: "Plan & Billing", icon: CreditCard },
      { to: "/app/my-notifications", label: "My Notifications", icon: Bell },
    ]},
  ],
  employee: [
    { to: "/app/me", label: "My Service CV", icon: UserCircle },
    { to: "/app/leaderboards", label: "Leaderboards", icon: Trophy },
    { to: "/app/training", label: "My Training", icon: GraduationCap },
  ],
  master_admin: [
    { section: "Overview", items: [
      { to: "/app/platform", label: "Overview", icon: Globe },
      { to: "/app/platform/live", label: "Live Dashboard", icon: Activity },
      { to: "/app/platform/system-report", label: "System Report", icon: BarChart3 },
    ]},
    { section: "Tenants", items: [
      { to: "/app/platform/organizations", label: "Organisations", icon: Building2 },
      { to: "/app/platform/subscriptions", label: "Tiers & Billing", icon: CreditCard },
      { to: "/app/platform/billing", label: "Billing Ledger", icon: Wallet },
    ]},
    { section: "Growth", items: [
      { to: "/app/platform/emails", label: "Email Campaigns", icon: Mail },
      { to: "/app/platform/ai-usage", label: "AI Credit Usage", icon: Sparkles },
      { to: "/app/platform/blog", label: "Insights Blog", icon: FileText },
      { to: "/app/platform/merchandise", label: "Merchandise", icon: Package },
    ]},
    { section: "Operations", items: [
      { to: "/app/platform/operations", label: "Operations", icon: Wrench },
      { to: "/app/platform/finance", label: "Finance", icon: Wallet },
      { to: "/app/platform/reports", label: "Reports", icon: FileText },
      { to: "/app/platform/staff", label: "Team", icon: UserCog },
      { to: "/app/platform/email", label: "Email Delivery", icon: Mail },
    ]},
  ],
  platform_sales: [
    { to: "/app/platform/organizations", label: "Organisations", icon: Building2 },
    { to: "/app/platform/subscriptions", label: "Subscriptions", icon: CreditCard },
    { to: "/app/platform/merchandise", label: "Merchandise", icon: Package },
  ],
  platform_ops: [
    { to: "/app/platform/operations", label: "Operations", icon: Wrench },
    { to: "/app/platform/organizations", label: "Organisations", icon: Building2 },
    { to: "/app/platform/merchandise", label: "Merchandise", icon: Package },
    { to: "/app/platform/blog", label: "Insights Blog", icon: FileText },
  ],
  platform_finance: [
    { to: "/app/platform/finance", label: "Finance", icon: Wallet },
    { to: "/app/platform/billing", label: "Billing Ledger", icon: CreditCard },
    { to: "/app/platform/reports", label: "Reports", icon: FileText },
    { to: "/app/platform/subscriptions", label: "Subscriptions", icon: CreditCard },
    { to: "/app/platform/merchandise", label: "Merchandise", icon: Package },
    { to: "/app/platform/blog", label: "Insights Blog", icon: FileText },
  ],
};

export function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[#F8FAFC]" data-testid="app-splash">
        <Logo stacked className="animate-pulse" />
        <div className="h-1 w-32 overflow-hidden rounded-full bg-slate-200">
          <div className="h-full w-1/2 animate-[loading_1.1s_ease-in-out_infinite] rounded-full bg-[#4338CA]" />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

export default function AppLayout({ children }) {
  const { user, logout, refresh } = useAuth();
  usePageMeta("Servantage", "/app", true);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pinned, togglePin] = usePinnedSidebar(setOpen);
  const [viewMode, setView] = useViewportMode();
  const tzReady = useTimeZonesReady(user?.org_id);
  const { notes, org, locName, billing } = useLayoutData(user?.role);
  const impersonator = user?.impersonator;
  const items = NAV[user?.role] || [];
  const grouped = Array.isArray(items) && items[0]?.items;
  const isAdmin = ["corporate_admin", "store_admin"].includes(user?.role);
  const { allNotes, muted, toggleMute, markLiveRead } = useLiveNotes(isAdmin, notes);
  const close = () => setOpen(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className={`min-h-screen bg-[#F8FAFC] ${pinned ? "md:flex" : ""}`}>
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-slate-200 bg-white transition-transform ${open ? "translate-x-0" : "-translate-x-full"} ${pinned ? "md:static md:translate-x-0" : ""}`}>
        <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5">
          <Logo />
          <div className="flex items-center gap-1">
            <button
              className="hidden rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-indigo-600 md:inline-flex"
              onClick={togglePin} data-testid="sidebar-pin"
              title={pinned ? "Unpin menu — free up space" : "Pin menu open"}>
              {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </button>
            <button className={pinned ? "md:hidden" : ""} onClick={() => setOpen(false)} data-testid="sidebar-close"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <nav className="sv-scroll flex h-[calc(100vh-4rem)] flex-col gap-1 overflow-y-auto p-3">
          {grouped ? (
            items.map((grp) => (
              <NavGroup key={grp.section} section={grp.section} items={grp.items} onNavigate={() => setOpen(false)} />
            ))
          ) : (
            items.map((it) => (
              <NavItem key={it.to} it={it} onNavigate={() => setOpen(false)} />
            ))
          )}
          {billing?.subscription && <PlanBadge billing={billing} isCorp={user?.role === "corporate_admin"} onNavigate={close} />}
          <SidebarAccount phoneAuth={user?.auth_type === "phone"} onLogout={doLogout} />
        </nav>
      </aside>

      {open && <div className={`fixed inset-0 z-30 bg-black/30 ${pinned ? "md:hidden" : ""}`} onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {impersonator && <ImpersonationBanner name={org?.name || user?.name} impersonator={impersonator} refresh={refresh} />}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur md:px-8">
          <div className="flex items-center gap-3">
            <button className={`rounded-md p-1 text-slate-600 transition-colors hover:bg-slate-100 ${pinned ? "md:hidden" : ""}`} onClick={() => setOpen(true)} data-testid="sidebar-open"><Menu className="h-6 w-6" /></button>
            <HeaderOrg org={org} locName={locName} />
          </div>
          <div className="flex items-center gap-3">
            <ViewToggle viewMode={viewMode} setView={setView} />
            {isAdmin && <SoundToggle muted={muted} toggleMute={toggleMute} />}
            <NotificationsMenu allNotes={allNotes} isAdmin={isAdmin} onOpen={markLiveRead} />
            <UserChip user={user} />
          </div>
        </header>
        <main className="sv-scroll flex-1 overflow-y-auto p-4 md:p-8">{tzReady ? children : null}</main>
      </div>
      <InstallPrompt />
      {user?.role === "corporate_admin" && <OnboardingWizard />}
    </div>
  );
}
